import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { signsAPI } from "../api";

type SubCategory = {
  id: string;
  title: string;
  emoji: string;
  count?: number;
};

type Category = {
  id: string;
  title: string;
  emoji: string;
  color: string;
  bg: string;
  sub: SubCategory[];
};

const SignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    signsAPI
      .getCategories()
      .then((res) => setCategories(res.data ?? []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* Top bar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: 520,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              padding: 0,
            }}>
            <ArrowLeft size={16} /> Orqaga
          </button>

          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            Belgilar
          </span>
        </div>
      </div>

      {/* Content */}
      <div
        style={{ maxWidth: 520, margin: "0 auto", padding: "68px 16px 32px" }}>
        {loading ?
          <div
            style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <Loader2 className="animate-spin text-blue-500" size={24} />
          </div>
        : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {categories.map((cat) => {
              const isOpen = expanded === cat.id;

              return (
                <div key={cat.id}>
                  {/* Category header */}
                  <button
                    onClick={() => setExpanded(isOpen ? null : cat.id)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "15px 16px",
                      borderRadius: isOpen ? "14px 14px 0 0" : "14px",
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderBottom:
                        isOpen ? "none" : "1px solid var(--border)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: cat.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 20,
                        flexShrink: 0,
                      }}>
                      {cat.emoji}
                    </div>

                    <span
                      style={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 14,
                        flex: 1,
                      }}>
                      {cat.title}
                    </span>

                    <ChevronRight
                      size={16}
                      style={{
                        color: "var(--muted)",
                        transform: isOpen ? "rotate(90deg)" : "none",
                        transition: "transform .15s",
                      }}
                    />
                  </button>

                  {/* Subcategories */}
                  {isOpen && (
                    <div
                      style={{
                        background: "var(--card2)",
                        border: "1px solid var(--border)",
                        borderTop: "none",
                        borderRadius: "0 0 14px 14px",
                        overflow: "hidden",
                      }}>
                      {cat.sub.map((sub, si) => (
                        <button
                          key={sub.id}
                          onClick={() =>
                            navigate(`/quiz/signs/${cat.id}/${sub.id}`)
                          }
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "12px 16px",
                            borderBottom:
                              si < cat.sub.length - 1 ?
                                "1px solid var(--border)"
                              : "none",
                            background: "transparent",
                            cursor: "pointer",
                            textAlign: "left",
                          }}>
                          <span
                            style={{
                              fontSize: 16,
                              width: 24,
                              textAlign: "center",
                            }}>
                            {sub.emoji}
                          </span>

                          <span
                            style={{
                              color: "#E2E8F0",
                              fontSize: 13,
                              fontWeight: 600,
                              flex: 1,
                            }}>
                            {sub.title}
                          </span>

                          <ChevronRight
                            size={13}
                            style={{ color: "var(--muted)" }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        }
      </div>
    </div>
  );
};

export default SignsPage;
